import React, { useState, useRef, useEffect, useId, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, X, Search } from 'lucide-react';
import useDropdownPlacement from '../hooks/useDropdownPlacement';
import { markDialogDirty } from '../hooks/useDialog';

/**
 * Styled single-select dropdown — the app-wide replacement for a native `<select>`, whose option
 * list is drawn by the OS and looks nothing like the rest of the UI.
 *
 * Accepts either `[{ value, label, disabled? }]` or a plain array of strings (in which case the
 * string is both value and label).
 *
 * A search box appears only once the list is long enough to be worth filtering (see SEARCH_AT), so
 * a three-option picker stays a three-option picker. The explicit close button follows the same
 * rule: on a long, browsable list you may want to back out without choosing, whereas on a short one
 * picking an option IS the way out and an X is just clutter. Escape and click-outside always close.
 *
 * Keyboard focus never goes INTO the list, the way it never goes into a native select's: the arrow
 * keys move through the options, Enter picks, Escape backs out and Tab moves on to the next field.
 * The options and the X are therefore not tab stops, and closing from the keyboard hands focus back
 * to the field so the next Tab carries on from where the user was.
 *
 * The panel is rendered into a PORTAL on document.body, positioned against the button. It used to
 * be absolutely positioned inside the wrapper, which meant a short modal boxed it in: an
 * eight-option list near the bottom of the Register Supply dialog had roughly four rows of room in
 * either direction, so it opened cramped and scrolling however the height was clamped. Out in the
 * portal only the viewport bounds it.
 *
 * It does, however, know where the viewport edge and its clipping ancestors are — see
 * useDropdownPlacement — so the list flips above the field, or shrinks, rather than opening into a
 * region that is cut off.
 */
const SEARCH_AT = 8;
const LIST_MAX = 240;   // matches the old max-h-60
const LIST_MIN = 160;   // below this, shrinking stops being useful — flip instead
const ROW_H = 36;       // one option row, for estimating the height we'd like
const SEARCH_H = 45;    // the search row, when shown

const Select = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  error,
  disabled = false,
  required = false,
  className = '',
  buttonClassName = '',
  id,
}) => {
  // A label with no id was never tied to anything: `htmlFor={undefined}` associates nothing, so a
  // labelled dropdown announced itself as just its current value and a screen reader never said
  // what the field was. Fall back to a generated id so every labelled Select is named.
  const generatedId = useId();
  const fieldId = id || generatedId;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(-1);
  // The scrolling option list. Arrowing past the visible rows has to bring them into view, or the
  // selection walks off the bottom of a list that is taller than its panel and the user is moving
  // something they cannot see.
  const listRef = useRef(null);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const searchRef = useRef(null);
  const panelRef = useRef(null);

  // Closing from the keyboard returns focus to the field. Left to itself, focus fell to <body> as the
  // panel unmounted, and the next Tab started again from the top of the page.
  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  const normalised = useMemo(
    () =>
      (options || []).map((o) =>
        typeof o === 'string' || typeof o === 'number'
          ? { value: o, label: String(o) }
          : { value: o.value, label: o.label ?? String(o.value), disabled: o.disabled }
      ),
    [options]
  );

  const searchable = normalised.length > SEARCH_AT;
  const filtered = useMemo(
    () =>
      query.trim()
        ? normalised.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
        : normalised,
    [normalised, query]
  );

  const selected = normalised.find((o) => String(o.value) === String(value));

  // Filtering invalidates the old index. Sitting on the first match means typing a few letters and
  // pressing Enter picks what you were obviously aiming at.
  useEffect(() => {
    if (open && query) setHighlight(filtered.length > 0 ? 0 : -1);
  }, [query, open, filtered.length]);

  // Keep the highlighted row on screen. Queried by index attribute rather than child position so
  // the "No matches" row can never be mistaken for an option.
  useEffect(() => {
    if (!open || highlight < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    // Against the full list, not `filtered`: on this render that still holds the last search (the
    // query is only being cleared now), so its index pointed at a different row once the whole list
    // came back, and Enter then silently picked that row.
    setHighlight(normalised.findIndex((o) => String(o.value) === String(value)));
    if (searchable) setTimeout(() => searchRef.current?.focus(), 0);
    const onDocMouseDown = (e) => {
      // The panel is portalled, so it is NOT inside the wrapper: without checking it too, clicking
      // an option would count as clicking outside and close the list before the option was chosen.
      const inAnchor = wrapperRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inAnchor && !inPanel) setOpen(false);
    };
    // Escape is caught on the document, in the capture phase, rather than on the button and the
    // search box alone. Focus is not always on one of those: a press on an option that never becomes
    // a click (the pointer moves before release) leaves focus on that option, and an Escape from
    // there reached no handler, so the list sat open. Stopping the event also means this Escape
    // closes the list and nothing else; a modal behind it that closes on Escape needs a second press.
    const onDocKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onDocKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onDocKeyDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chrome = searchable ? SEARCH_H : 0;
  const { dropUp, space, rect } = useDropdownPlacement(open && !disabled, wrapperRef, {
    wantHeight: chrome + Math.min(normalised.length * ROW_H + 8, LIST_MAX),
    minHeight: chrome + LIST_MIN,
    ignoreClippers: true,
  });
  // Never taller than the room actually measured. LIST_MIN is about deciding whether flipping beats
  // shrinking (see useDropdownPlacement) — using it as a floor HERE forced the panel into a gap that
  // could not hold it, so in a short modal it opened upward and had its top cut off by the header.
  // A one-row floor keeps a degenerate zero-height list from appearing.
  const listMax = Math.max(ROW_H, Math.min(LIST_MAX, space - chrome));

  const commit = (opt, fromKeyboard = false) => {
    if (opt.disabled) return;
    // A pick fires no input event, so a dialog this sits in is told directly that its form changed.
    if (String(opt.value) !== String(value)) markDialogDirty(wrapperRef.current);
    onChange(opt.value);
    // A keyboard pick keeps the user's place in the form. A mouse pick leaves focus alone, so the
    // field does not light up with a focus ring after every click.
    if (fromKeyboard) close();
    else setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault(); setOpen(true); return;
    }
    if (!open) return;
    // Tab closes the list and moves on. Focus goes back to the field first and the key is
    // deliberately NOT prevented, so the browser's own Tab continues from the field. Left alone it
    // would continue from the search box, which lives in a portal at the very end of the page, and
    // the next stop after that is the browser's address bar.
    if (e.key === 'Tab') { close(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlight]) commit(filtered[highlight], true); }
  };

  return (
    <div className={className}>
      {label && (
        <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative" ref={wrapperRef}>
        <button
          ref={buttonRef}
          id={fieldId}
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={onKeyDown}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm text-left bg-white border rounded-lg transition-colors
            focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400
            disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed
            ${error ? 'border-red-300' : open ? 'border-emerald-400' : 'border-gray-300 hover:border-gray-400'} ${buttonClassName}`}
        >
          {/* Long values are truncated to keep the control on one line; the title makes the full
              value readable on hover so a cropped label is never the only way to see it. */}
          <span
            className={`truncate ${selected ? 'text-gray-900' : 'text-gray-400'}`}
            title={selected ? String(selected.label) : undefined}
          >
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && !disabled && rect && createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              left: rect.left,
              width: rect.width,
              // Anchored to whichever edge it opens from, so it stays put while the page behind it
              // scrolls (the placement hook re-measures on scroll and resize).
              ...(dropUp
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.bottom + 4 }),
              // Above the modal it may be opened from, which sits at z-50.
              zIndex: 70,
            }}
            className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
          >
            {searchable && (
              <div className="flex items-center gap-2 px-2.5 py-2 border-b border-gray-100">
                <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Search…"
                  className="flex-1 min-w-0 text-sm outline-none"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setOpen(false)}
                  className="p-1 rounded-md hover:bg-gray-100 text-gray-400 flex-shrink-0"
                  title="Close"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <div ref={listRef} className="overflow-y-auto py-1" style={{ maxHeight: listMax }}>
              {filtered.length === 0 ? (
                <div className="px-3 py-2.5 text-sm text-gray-400">No matches</div>
              ) : (
                filtered.map((o, i) => {
                  const isSelected = String(o.value) === String(value);
                  return (
                    <button
                      key={`${o.value}-${i}`}
                      type="button"
                      tabIndex={-1}
                      disabled={o.disabled}
                      data-idx={i}
                      onMouseEnter={() => setHighlight(i)}
                      onClick={() => commit(o)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors
                        disabled:text-gray-300 disabled:cursor-not-allowed
                        ${highlight === i
                          ? 'bg-emerald-600 text-white font-medium'
                          : isSelected
                            ? 'bg-emerald-50 text-emerald-800 font-medium'
                            : 'text-gray-700'}`}
                    >
                      {/* Wraps rather than truncates. The panel is pinned to the trigger's width,
                          so a label longer than a narrow control (e.g. "Until Finished (ختم ہونے
                          تک)" in a third-width field) was being cut off with no way to read it —
                          and an option you cannot read is not an option. */}
                      <span className="whitespace-normal break-words">{o.label}</span>
                      {isSelected && (
                        <Check className={`h-4 w-4 flex-shrink-0 ${highlight === i ? 'text-white' : 'text-emerald-600'}`} />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default Select;
